import { Box, Group, Stack, TextInput } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import type {
	ProviderName,
	SAMLConfigurationSchemaIn,
	SAMLConfigurationSchemaOut,
} from '@repo/api-codegen';
import {
	useApiCreateSamlConfiguration,
	useApiListAllowedDomains,
	useApiListSamlConfigurations,
	useApiUpdateSamlConfiguration,
} from '@repo/api-codegen';
import { SettingsBlock, SettingsSelect } from '@repo/common/components';
import { Banner, Button, CopyButton, Text } from '@repo/foundations';
import { FormikProvider, useFormik } from 'formik';
import { useMemo } from 'react';
import short from 'short-uuid';
import * as Yup from 'yup';
import { useAuthUser } from '../../api';
import { SSO_NAMES } from '../Auth/utils';

const samlOptions: { value: ProviderName; label: string }[] = [
	{ value: 'generic', label: SSO_NAMES.generic },
	{ value: 'google', label: SSO_NAMES.google },
	{ value: 'microsoft', label: SSO_NAMES.microsoft },
	{ value: 'okta', label: SSO_NAMES.okta },
	{ value: 'onelogin', label: SSO_NAMES.onelogin },
];

const lowercaseSlugTranslator = short('abcdefghijklmnopqrstuvwxyz0123456789');
const ENTITY_ID_LABEL = 'Entity ID (Identifier)';

const validationSchema = Yup.object().shape({
	provider_name: Yup.string()
		.required('SAML provider is required')
		.when('metadata_url', (metadataUrl, schema) => {
			if (metadataUrl?.toLowerCase().includes('microsoft')) {
				return schema.oneOf(
					['microsoft'],
					'Provider must be set to Microsoft based on the provided Metadata URL'
				);
			}
			if (metadataUrl?.toLowerCase().includes('okta')) {
				return schema.oneOf(
					['okta'],
					'Provider must be set to Okta based on the provided Metadata URL'
				);
			}
			if (metadataUrl?.toLowerCase().includes('google')) {
				return schema.oneOf(
					['google'],
					'Provider must be set to Google based on the provided Metadata URL'
				);
			}
			if (metadataUrl?.toLowerCase().includes('onelogin')) {
				return schema.oneOf(
					['onelogin'],
					'Provider must be set to OneLogin based on the provided Metadata URL'
				);
			}
			return schema;
		}),
	metadata_url: Yup.string()
		.url('Must be a valid URL')
		.required('Metadata URL is required'),
	entity_id: Yup.string().required('Entity ID is required'),
});

type SamlFormValues = SAMLConfigurationSchemaIn;

export function SamlSettingsForm() {
	const { domain, workspace } = useAuthUser();
	const {
		data: samlConfigsData,
		isLoading: isLoadingConfigs,
		refetch: refetchSamlConfigs,
	} = useApiListSamlConfigurations({});

	const { data: allowedDomainsData } = useApiListAllowedDomains({});

	const existingConfig: SAMLConfigurationSchemaOut | undefined = useMemo(
		() => samlConfigsData?.items?.[0],
		[samlConfigsData]
	);

	const { mutateAsync: createSamlConfig, isPending: isCreating } =
		useApiCreateSamlConfiguration();
	const { mutateAsync: updateSamlConfig, isPending: isUpdating } =
		useApiUpdateSamlConfiguration();

	const isLoading = isCreating || isUpdating;

	// Occasionally, the workspace ID is not available (i.e. if we update the AuthUser) so we
	// pass an empty string to the translator as a backup
	const acsPath = lowercaseSlugTranslator.fromUUID(workspace.id ?? '');
	const acsPathFull = `${window.location.origin}/api/v1/auth/saml/${acsPath}/acs/`;

	const formik = useFormik<SamlFormValues>({
		initialValues: {
			provider_name:
				(existingConfig?.provider_name as ProviderName) ?? 'generic',
			acs_path: acsPath,
			entity_id:
				existingConfig?.entity_id ?? `https://${domain}/secoda/${acsPath}`,
			metadata_url: existingConfig?.metadata_url ?? '',
		},
		validationSchema,
		enableReinitialize: true,
		onSubmit: async (values) => {
			const payload: SAMLConfigurationSchemaIn = {
				provider_name: values.provider_name ?? 'generic',
				acs_path: values.acs_path || acsPath,
				entity_id: values.entity_id || `https://${domain}/secoda/${acsPath}`,
				metadata_url: values.metadata_url || '',
			};

			try {
				if (existingConfig?.id) {
					await updateSamlConfig({
						pathParams: {
							samlConfigId: existingConfig.id,
						},
						body: payload,
					});
					showNotification({
						title: 'Success',
						message: 'SAML configuration updated.',
						color: 'green',
					});
				} else {
					await createSamlConfig({
						body: payload,
					});
					showNotification({
						title: 'Success',
						message: 'SAML configuration created.',
						color: 'green',
					});
				}
				refetchSamlConfigs();
			} catch (error) {
				showNotification({
					title: 'Error',
					message:
						`Failed to ${existingConfig?.id ? 'update' : 'create'} SAML configuration. ` +
						JSON.stringify(error),
					color: 'red',
				});
			}
		},
	});

	const isMissingAllowedDomains =
		allowedDomainsData?.allowed_domains?.length === 0 ||
		(allowedDomainsData?.allowed_domains?.length === 1 &&
			allowedDomainsData?.allowed_domains?.includes('secoda.co'));

	return (
		<FormikProvider value={formik}>
			<Box mb={'sm'}>
				{existingConfig?.id && (
					<Banner
						tone={isMissingAllowedDomains ? 'warning' : 'info'}
						title="SAML Access"
						message="Email addresses in allowed domains may sign in with SAML"
					/>
				)}
			</Box>
			<form onSubmit={formik.handleSubmit}>
				<Stack spacing={0}>
					<SettingsSelect
						title="SAML provider (IdP)"
						description="The identity provider (IdP) you would like to use for SAML authentication"
						options={samlOptions}
						value={formik.values.provider_name ?? 'generic'}
						onChange={(value) => formik.setFieldValue('provider_name', value)}
						disabled={isLoadingConfigs || isLoading}
					/>
					<SettingsBlock
						title="ACS URL"
						description="The Assertion Consumer Service (ACS) URL to input into your IdP"
					>
						<TextInput
							value={acsPathFull}
							readOnly
							variant="filled"
							rightSection={<CopyButton value={acsPathFull} />}
							disabled={isLoadingConfigs || isLoading}
						/>
					</SettingsBlock>
					<SettingsBlock
						title={ENTITY_ID_LABEL}
						description="The Entity/Audience/Application ID to input into your IdP"
					>
						<TextInput
							value={formik.values.entity_id}
							variant="filled"
							rightSection={<CopyButton value={formik.values.entity_id} />}
							disabled={isLoadingConfigs || isLoading}
						/>
					</SettingsBlock>
					<SettingsBlock
						title="Metadata URL"
						description="This is a public URL provided by your IdP once you have configured your SAML application"
					>
						<Group>
							<TextInput
								style={{ flex: 1 }}
								placeholder="https://idp.com/metadata.xml"
								{...formik.getFieldProps('metadata_url')}
								disabled={isLoadingConfigs || isLoading}
							/>
							<Button
								size="md"
								type="submit"
								variant="primary"
								loading={isLoading}
								disabled={isLoadingConfigs || !formik.dirty || !formik.isValid}
							>
								{existingConfig?.id ? 'Update SAML' : 'Save SAML Configuration'}
							</Button>
						</Group>
						{!formik.isValid && (
							<Stack spacing="xs" mt="xs">
								{Object.values(formik.errors).map((error) => (
									<Text key={error} color="text/critical/default" size="sm">
										{error}
									</Text>
								))}
							</Stack>
						)}
					</SettingsBlock>
				</Stack>
			</form>
		</FormikProvider>
	);
}
